<template>
  <component
    :is="allowRating ? 'base-grid' : 'div'"
    :cols="1"
    :x-gap="2"
    :y-gap="2"
  >
    <template v-if="allowRating">
      <base-button
        v-if="!negativeRatingsAllowed"
        x-large
        :class="Classes.DETAIL_THUMBS_UP_BUTTON"
        full-width
        icon-size="2xl"
        :active="liked === RATINGS_LIKE"
        :secondary="liked !== RATINGS_LIKE"
        @click="updateRating(RATINGS_LIKE)"
      >
        <div class="tw-flex tw-space-x-2">
          <font-awesome-icon
            icon="thumbs-up"
            :class="`${Classes.DETAIL_THUMBS_UP_ICON} tw-h-6 tw-w-6`"
          />
          <p class="tw-text-h5">{{ numberFormatter(positive) }}</p>
        </div>
      </base-button>

      <div
        v-else
        class="tw-relative game-tile-background-color game-tile-radius game-tile-border tw-col-start-1 tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-between tw-h-32 tw-p-4"
        :class="Classes.SECTION"
      >
        <rating-bar v-if="negativeRatingsAllowed" :mod="mod" />
        <div class="tw-flex tw-w-full">
          <tooltip-hover
            class="tw-w-1/2 tw-pr-[10px]"
            :text="
              canRate
                ? `${$t('like')} ${mod?.name}`
                : $t('marketplace.purchase_to_rate', {
                    ugc: ugcName(game, false, true).value,
                  })
            "
            position="bottom"
          >
            <base-button
              :class="Classes.DETAIL_THUMBS_UP_BUTTON"
              full-width
              icon="thumbs-up"
              :disabled="!canRate"
              :success="liked === RATINGS_LIKE"
              :secondary="liked !== RATINGS_LIKE"
              @click="updateRating(RATINGS_LIKE)"
            >
              {{ numberFormatter(positive) }}
            </base-button>
          </tooltip-hover>

          <tooltip-hover
            class="tw-w-1/2 tw-pl-[10px]"
            :text="
              canRate
                ? `${$t('dislike')} ${mod?.name}`
                : $t('marketplace.purchase_to_rate', {
                    ugc: ugcName(game, false, true).value,
                  })
            "
            position="bottom"
          >
            <base-button
              :class="Classes.DETAIL_THUMBS_DOWN_BUTTON"
              full-width
              icon="thumbs-down"
              :disabled="!canRate"
              :danger="liked === RATINGS_DISLIKE"
              :secondary="liked !== RATINGS_DISLIKE"
              @click="updateRating(RATINGS_DISLIKE)"
            >
              {{ numberFormatter(negative) }}
            </base-button>
          </tooltip-hover>
        </div>
      </div>
    </template>
  </component>
</template>

<script>
import ViewFlagButton from '@components/View/ViewFlagButton.vue'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { useResourceValue, usePermissions } from '@composables'
import { allowNegativeRatings } from '@helpers/bitHelper.js'
import { setGlobalModal } from '@composables/useModal.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import RatingBar from '@components/Mod/RatingBar.vue'
import { numberFormatter } from '@helpers/utils.js'
import { purchasesStore, authStore } from '@stores'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { computed, toRefs } from 'vue'
import {
  RATINGS_UNDECIDED,
  RATINGS_DISLIKE,
  RATINGS_LIKE,
} from '@config/options.js'
export default {
  components: {
    ViewFlagButton,
    TooltipHover,
    RatingBar,
  },
  props: {
    liked: {
      type: Number,
      default: RATINGS_UNDECIDED,
    },
    positive: {
      type: Number,
      default: null,
    },
    negative: {
      type: Number,
      default: null,
    },
    mod: {
      type: Object,
      default: null,
    },
    game: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:rating'],
  setup(props, { emit }) {
    const { game, mod, liked } = toRefs(props)
    const { isLoggedIn } = authStore()
    const { modOwned } = purchasesStore()

    const { hasPermissionForMod } = usePermissions()
    const { ugcName } = useResourceValue()

    const isModModerator = computed(() =>
      hasPermissionForMod(mod.value.name_id, game.value.name_id, 'moderator')
    )

    const allowRating = computed(() => Settings.value.allowRating)

    const canRate = computed(
      () => !mod.value?.price || isModModerator.value || modOwned(mod.value.id)
    )

    const negativeRatingsAllowed = computed(() =>
      allowNegativeRatings(game.value?.community_options)
    )

    function updateRating(update) {
      if (!isLoggedIn.value) {
        setGlobalModal({ modal: MODAL_LOGIN })
        return
      }

      if (liked.value === RATINGS_UNDECIDED) {
        emit('update:rating', update)
      } else if (liked.value === RATINGS_LIKE && update === RATINGS_DISLIKE) {
        emit('update:rating', RATINGS_DISLIKE)
      } else if (liked.value === RATINGS_DISLIKE && update === RATINGS_LIKE) {
        emit('update:rating', RATINGS_LIKE)
      } else if (liked.value === update) {
        emit('update:rating', RATINGS_UNDECIDED)
      }
    }

    return {
      negativeRatingsAllowed,
      numberFormatter,
      RATINGS_DISLIKE,
      updateRating,
      RATINGS_LIKE,
      allowRating,
      canRate,
      ugcName,
      Classes,
    }
  },
}
</script>
